<template>
  <div>
    <div class="position-relative">
      <div class="workspace-title">
        Team profile
      </div>
    </div>
    <b-row>
      <b-col tag="main" lg="12" xl="9" class="mb-4">
        <div v-if="initialized">
          <team-profile-info
            :team="teamProfile"
            :team-kudos="computedTeamKudos"
            :members="teamMembers"
            :editable="canEdit"
            @edit="onEditProfile"
          >
            <team-members-list
              :team="teamProfile"
              :profiles="profiles"
              :profile-me="profileMe"
              :user-kudos="allUserKudos"
              :user-thanks="allUserThanks"
              class="mt-5"
              @select="onSelectTeamMemeber"
              @give-kudo="onGiveKudo"
              @send-message="onSendMessage"
            />
            <b-modal ref="givekudo" hide-footer size="lg" title="Give a kudo">
              <give-kudo
                v-if="selectedProfile"
                :receiver="selectedProfile"
                receiver-type="user"
                @submitted="onKudoSubmitted"
              />
            </b-modal>
            <b-modal ref="sendmsg" hide-footer size="lg" title="New message">
              <send-message
                :receivers="sendmsg"
                @submitted="onMsgSubmitted"
              />
            </b-modal>
          </team-profile-info>
          <b-modal
            v-if="canEdit"
            ref="editprofile"
            hide-footer
            size="lg"
            title="Edit Team Profile"
            @hide="onModalHide(process.editTeam, $event)"
          >
            <edit-team-profile
              :team="teamProfile"
              :profiles="profiles"
              :profile-me="profileMe"
              :active-tab="editProfileActiveTab"
              @before-submit="process.editTeam=true"
              @submit-failed="process.editTeam=false"
              @submitted="process.editTeam=false; $refs.editprofile.hide()"
            />
            <b-overlay :show="process.editTeam" no-wrap opacity=".5" />
          </b-modal>
          <kudos-received
            :loading="isAllKudosLoading"
            :kudos="kudos"
            :received="kudosReceived"
            class="bg-white shadow-sm p-5"
          />
          <feedbacks-list
            :loading="isFeedbacksTeamLoading"
            :feedbacks="computedFeedbacks"
            class="mt-4"
          />
        </div>
        <div v-else class="text-center p-5">
          <b-spinner variant="primary" label="Loading..." />
        </div>
      </b-col>
      <b-col lg="12" xl="3">
        <b-row>
          <b-col tag="aside" lg="6" xl="12" class="mb-4">
            <team-members-location :members="teamMembers" class="shadow h-100" />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TeamProfileInfo from '@/components/TeamProfile'
import FeedbacksList from '@/components/FeedbacksList'
import TeamMembersList from '@/components/TeamMembersList'
import EditTeamProfile from './EditTeamProfile.vue'
import { KudosReceived } from '@/components/KudosList'
import SendMessage from './SendMessage.vue'
import GiveKudo from './GiveKudo'
import { RECEIVER_TYPE } from '@/helpers/types'
import { TeamMembersLocation } from './Aside'

export default {
  name: 'Team',

  computed: {
    ...mapGetters([
      'profileMe',
      'team',
      'teamProfiles',
      'profiles',
      'isKudosLoading',
      'kudos',
      'isTeamKudosLoading',
      'teamKudosReceived',
      'teamKudos',
      'allUserKudos',
      'allUserThanks',
      'feedbacksTeam',
      'isFeedbacksTeamLoading',
      'workspaceAdmins'
    ]),

    teamId() {
      return Number(this.$route.params.teamId)
    },

    teamProfile() {
      return this.team(this.teamId)
    },

    teamMembers() {
      return this.teamProfiles(this.teamId)
    },

    computedTeamKudos() {
      return this.isTeamKudosLoading ? undefined : this.teamKudos(this.teamId)
    },

    computedFeedbacks() {
      return this.isFeedbacksTeamLoading ? undefined : this.feedbacksTeam(this.teamId)
    },

    kudosReceived() {
      return this.isTeamKudosLoading ? undefined : this.teamKudosReceived(this.teamId)
    },

    isAllKudosLoading() {
      return this.isTeamKudosLoading || this.isKudosLoading
    },

    canEdit() {
      if (this.initialized && this.teamProfile) {
        const isAdmin = this.workspaceAdmins.indexOf(this.profileMe.id) !== -1
        const isManager = (this.teamProfile.members.find(p => p.profile === this.profileMe.id) || {}).isManager
        return isAdmin || isManager
      } else {
        return false
      }
    }
  },

  watch: {
    teamId() {
      this.load()
    }
  },

  data() {
    return {
      initialized: false,
      editProfileActiveTab: 0,
      selectedProfile: null,
      sendmsg: [],
      process: {
        editTeam: false
      }
    }
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      this.initialized = false
      this.$store.dispatch('fetchTeam', this.teamId)
        .then(() => (this.initialized = true))

      this.$store.dispatch('fetchKudosIfNot')
      this.$store.dispatch('fetchTeamKudos', this.teamId)
      this.$store.dispatch('fetchTeamFeedbacks', this.teamId)
    },

    onModalHide(process, e) {
      if (process) {
        e.preventDefault()
      }
    },

    onEditProfile({ from }) {
      let activeTab = 0
      switch (from) {
        case 'facts':
        case 'highlight':
          activeTab = 1
          break
        case 'values':
          activeTab = 3
          break
      }

      this.editProfileActiveTab = activeTab
      this.$refs.editprofile.show()
    },

    onSelectTeamMemeber(profile) {
      this.selectedProfile = profile
      this.$store.dispatch('fetchUserKudos', profile.id)
      this.$store.dispatch('fetchUserThanks', profile.id)
    },

    onGiveKudo(profile) {
      this.selectedProfile = profile
      this.$refs.givekudo.show()
    },

    onSendMessage(profile) {
      this.sendmsg = [{ receiver: profile, type: RECEIVER_TYPE.USER }]
      this.$refs.sendmsg.show()
    },

    onKudoSubmitted() {
      this.$refs.givekudo.hide()
    },

    onMsgSubmitted() {
      this.$refs.sendmsg.hide()
    }
  },

  components: {
    TeamProfileInfo,
    TeamMembersList,
    FeedbacksList,
    EditTeamProfile,
    KudosReceived,
    TeamMembersLocation,
    SendMessage,
    GiveKudo
  }
}
</script>
